import React from "react";
import { QRCodeCanvas } from "qrcode.react";
import { useTranslation } from "react-i18next";
import ButtonHapticWrapper from "component/Button/ButtonHapticWrapper";

type InvitePanelProps = {
    lobbyUrl: string;
    lobbyCode: string;
    copiedLink: boolean;
    copiedCode: boolean;
    onCopyLink: () => void;
    onCopyCode: () => void;
    children?: React.ReactNode;
};

const InvitePanel = ({ lobbyUrl, lobbyCode, copiedLink, copiedCode, onCopyLink, onCopyCode, children }: InvitePanelProps) => {
    const { t } = useTranslation();

    return (
        <>
            <div className="hidden lg:block viewport-compact:block w-px self-stretch invite-separator"></div>

            <div className="hidden lg:flex viewport-compact:flex flex-col items-center justify-center gap-5 viewport-landscape:gap-[clamp(0.75rem,2.5vh,1.5rem)] w-56 viewport-landscape:w-[clamp(11.5rem,25vw,15rem)] whitespace-nowrap">
                <h2 className="font-extrabold text-base viewport-landscape:text-[clamp(0.75rem,2.5vh,1.125rem)] uppercase tracking-[0.2em] text-white/90 drop-shadow-sm">{t("lobby.invite_friends")}</h2>

                <div className="bg-white rounded-xl p-2.5 viewport-landscape:p-[clamp(0.375rem,1.5vh,0.75rem)] shadow-lg">
                    <QRCodeCanvas
                        value={lobbyUrl}
                        size={128}
                        level="L"
                        className="viewport-landscape:w-[clamp(6rem,16vh,9rem)] viewport-landscape:h-[clamp(6rem,16vh,9rem)]"
                    />
                </div>

                <div className="w-full h-px bg-white/10"></div>

                <div className="flex flex-col items-center gap-1">
                    <span className="text-white/40 text-[10px] font-semibold uppercase tracking-widest">{t("lobby.room_code")}</span>
                    <ButtonHapticWrapper>
                        <button onClick={onCopyCode} className="flex items-center gap-1.5 cursor-pointer group">
                            <span className="text-white font-extrabold text-lg viewport-compact:text-[clamp(0.875rem,3vh,1.125rem)] tracking-[0.15em] drop-shadow">{lobbyCode}</span>
                            {copiedCode ? (
                                <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                            ) : (
                                <svg className="w-4 h-4 text-white/20 group-hover:text-white/40 transition-colors duration-200" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <rect x="9" y="9" width="13" height="13" rx="2" />
                                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                                </svg>
                            )}
                        </button>
                    </ButtonHapticWrapper>
                </div>

                <div className="flex items-center gap-3 w-full">
                    <ButtonHapticWrapper>
                        <button onClick={onCopyLink} className="flex-1 flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 transition-colors duration-200 rounded-xl px-3 py-2.5 viewport-compact:py-[clamp(0.375rem,1.5vh,0.625rem)] cursor-pointer border border-white/10">
                            {copiedLink ? (
                                <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                            ) : (
                                <svg className="w-4 h-4 text-white/70" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                            )}
                            {!copiedLink && <span className="text-white/70 text-[11px] font-semibold">{t("lobby.link")}</span>}
                        </button>
                    </ButtonHapticWrapper>

                    <ButtonHapticWrapper>
                        <button onClick={() => { if(navigator.share) { navigator.share({ url: lobbyUrl }); } }} className="flex-1 flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 transition-colors duration-200 rounded-xl px-3 py-2.5 viewport-compact:py-[clamp(0.375rem,1.5vh,0.625rem)] cursor-pointer border border-white/10">
                            <svg className="w-4 h-4 text-white/70" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" /></svg>
                            <span className="text-white/70 text-[11px] font-semibold">{t("lobby.share")}</span>
                        </button>
                    </ButtonHapticWrapper>
                </div>

                {children}
            </div>
        </>
    );
};

export default InvitePanel;
